import {
  CoreBitlyFieldsFragment,
  CorePinterestBoardFieldsFragment,
  GenericProfileType,
  InstagramApiType,
  Network,
  SocialOAuthProvider,
  SocialProfileFieldsFragment,
  ThirdPartyProfileSearchType
} from '../../../models/graphql';
import { Reducer } from 'react';
import { Void } from '../../../models/basic-objects';
import {
  ConnectProfileOAuthType,
  FacebookReconnectAction,
  FacebookReconnectInitialState,
  FacebookReconnectType
} from './Profiles.controller';
import { SocialNetworksEnum } from '../../../constants/social-networks';
import { ApolloErrorMapTypesType } from '../../../models/apollo-errors';
import { showToast } from '../../../helpers/showToast';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import {
  SocialProfileComponentBasicType,
  SocialProfileComponentExtendedType
} from '../../../components/socialProfiles/SocialProfile/SocialProfile.view';
import {
  MappedProfileType,
  TikTokProfileSettingsType
} from '../../../components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import { BaseMention } from '../../../models/slate-editor';
import {
  ConnectMainSocialProfile_connectMainSocialProfile_socialProfile,
  GetMe_me_organizations,
  ReconnectSocialProfile_reconnectSocialProfile_socialProfile
} from '../../../models/legacy-graphql';
import { sortedSocialProfilesTypes } from '../../../apollo/socialProfilesPolicy';
import { SelectedSocialProfileValue } from 'components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { capitalize } from 'lodash';

const setNetworkAndCallOAuth: Void<ConnectProfileOAuthType> = (param: ConnectProfileOAuthType | undefined) => {
  if (param) {
    param.setLoading && param.setLoading(true);
    param
      .startSocialOAuth({
        variables: {
          oAuthProvider: param.oAuthProvider,
          profileNetwork: param.profileNetwork,
          context: param.socialOAuthContext
        }
      })
      .then(result => {
        if (result.data) {
          const { socialAuthToken, redirectUrl } = result.data.startSocialOAuth;
          sessionStorage.setItem(
            'socialOAuth',
            JSON.stringify({
              socialAuthToken,
              context: param.context,
              oAuthProvider: param.oAuthProvider,
              network: param.profileNetwork,
              socialOAuthContext: param.socialOAuthContext,
              ...(param.profile && { profile: param.profile })
            })
          );
          window.location.href = redirectUrl;
        }
      })
      .catch(e => {
        param.setLoading && param.setLoading(false);
        showToast({
          severity: 'error',
          detail: remapApolloErrors(e),
          summary: 'OAuth Error'
        });
        // prevents runtime error
      });
  }
};

const sortProfiles = (
  a: SocialProfileFieldsFragment | CoreBitlyFieldsFragment,
  b: SocialProfileFieldsFragment | CoreBitlyFieldsFragment
): -1 | 0 | 1 => {
  const profilesTypeOrder: Array<SocialProfileFieldsFragment['__typename'] | CoreBitlyFieldsFragment['__typename']> = [
    ...sortedSocialProfilesTypes,
    'Bitly'
  ];
  const atypePosition: number = profilesTypeOrder.findIndex(item => item === a.__typename);
  const btypePosition: number = profilesTypeOrder.findIndex(item => item === b.__typename);
  if (atypePosition > -1 && btypePosition > -1) {
    if (atypePosition === btypePosition) {
      // instagram
      if (a.__typename === 'InstagramSocialProfile' && b.__typename === 'InstagramSocialProfile') {
        const aIgType = a.type;
        const bIgType = b.type;
        // check for inner type, then alphabetically
        if (aIgType === bIgType) {
          if (a.displayName === b.displayName) {
            return 0;
          }
          return a.displayName > b.displayName ? 1 : -1;
        } else {
          return a.type > b.type ? 1 : -1;
        }
      }
      // facebook
      else if (a.__typename === 'FacebookSocialProfile' && b.__typename === 'FacebookSocialProfile') {
        const aFbType = a.type;
        const bFbType = b.type;
        // check for inner type, then alphabetically
        if (aFbType === bFbType) {
          if (a.displayName === b.displayName) {
            return 0;
          }
          return a.displayName > b.displayName ? 1 : -1;
        } else {
          // page or group
          return a.type === 'page' ? -1 : 1;
        }
      }
      // linkedin
      else if (a.__typename === 'LinkedInSocialProfile' && b.__typename === 'LinkedInSocialProfile') {
        const aLiType = a.type;
        const bLiType = b.type;
        // check for inner type, then alphabetically
        if (aLiType === bLiType) {
          if (a.displayName === b.displayName) {
            return 0;
          }
          return a.displayName > b.displayName ? 1 : -1;
        } else {
          // page or group
          return a.type === 'company' ? -1 : 1;
        }
      }
      // PN and TW and google and unknown
      else {
        if (a.displayName === b.displayName) {
          return 0;
        }
        return a.displayName > b.displayName ? 1 : -1;
      }
    }
    return atypePosition > btypePosition ? 1 : -1;
  }
  return 1;
};

const remapMentionTagsToSocialProfile = (tag: BaseMention): SocialProfileComponentExtendedType | null => {
  const { method } = tag;
  let provider: SocialOAuthProvider | null;
  let network: Network | null;
  let type = '';
  switch (method) {
    case ThirdPartyProfileSearchType.FACEBOOK_PAGE:
      provider = SocialOAuthProvider.FACEBOOK;
      network = Network.FACEBOOK;
      type = 'page';
      break;
    case ThirdPartyProfileSearchType.LINKEDIN_COMPANY:
      provider = SocialOAuthProvider.LINKEDIN;
      network = Network.LINKEDIN;
      type = 'company';
      break;
    case ThirdPartyProfileSearchType.TWITTER_USER:
      provider = SocialOAuthProvider.TWITTER;
      network = Network.TWITTER;
      break;
    default:
      provider = null;
      network = null;
  }
  if (provider === null || network === null) return null;
  return {
    id: tag.externalId || 'external-mocked-id',
    type,
    provider,
    network,
    name: tag.displayName
  };
};

const remapSocialProfile = (
  profile: MappedProfileType,
  returnPostAs: boolean,
  returnBoards: boolean,
  returnTikTok: boolean
): SocialProfileComponentExtendedType => {
  const provider =
    profile.__typename === 'InstagramSocialProfile' && profile.apiType === 'FACEBOOK_GRAPH'
      ? SocialOAuthProvider.FACEBOOK
      : mapOAuthFromTypename(profile.__typename);
  const network = mapNetworkFromTypename(profile.__typename);
  const generic = profile.__typename === 'GenericSocialProfile' ? profile.genericProfile : undefined;
  const name = profile.displayName;
  const id = profile.id;
  let type;
  let postAs: string | undefined;
  let pinterestBoards: CorePinterestBoardFieldsFragment[] | undefined;
  let tikTokSettings: TikTokProfileSettingsType | undefined;
  switch (profile.__typename) {
    case 'TwitterSocialProfile':
    case 'GoogleSocialProfile':
    case 'SocialProfile':
    default:
      type = '';
      break;
    case 'InstagramSocialProfile':
    case 'LinkedInSocialProfile':
      type = profile.type;
      break;
    case 'FacebookSocialProfile':
      type = profile.type;
      postAs = profile.postAs?.displayName || undefined;
      break;
    case 'PinterestSocialProfile':
      type = '';
      pinterestBoards = profile.boards ?? [];
      break;
    case 'TikTokSocialProfile':
      type = '';
      tikTokSettings = {
        privacy: profile.privacy,
        commentDisabled: profile.commentDisabled,
        duetDisabled: profile.duetDisabled,
        stitchDisabled: profile.stitchDisabled,
        maxVideoDuration: profile.maxVideoDuration,
        id: profile.id
      };
      break;
  }
  return {
    id,
    provider,
    network,
    genericProfile: generic,
    name,
    type,
    ...(pinterestBoards && returnBoards ? { pinterestBoards } : {}),
    ...(postAs && returnPostAs ? { postAs } : {}),
    ...(tikTokSettings && returnTikTok ? { tikTokSettings } : {})
  };
};

const getMentionType = (profile: SocialProfileFieldsFragment): null | ThirdPartyProfileSearchType => {
  switch (profile.__typename) {
    case 'FacebookSocialProfile':
      return profile.type === 'page' ? ThirdPartyProfileSearchType.FACEBOOK_PAGE : null;
    case 'TwitterSocialProfile':
      return ThirdPartyProfileSearchType.TWITTER_USER;
    case 'LinkedInSocialProfile':
      return profile.type === 'company' ? ThirdPartyProfileSearchType.LINKEDIN_COMPANY : null;
    default:
      return null;
  }
};

const mapGenericProfileToTypename = (genericType: GenericProfileType) => {
  switch (genericType) {
    case GenericProfileType.FACEBOOK_GROUP:
    case GenericProfileType.FACEBOOK_PERSONAL:
      return 'FacebookSocialProfile';
    case GenericProfileType.INSTAGRAM_PERSONAL:
      return 'InstagramSocialProfile';
    default:
      return null;
  }
};

const mapNetworkFromTypename = (
  typename: SocialProfileFieldsFragment['__typename'] | CoreBitlyFieldsFragment['__typename']
): Network => {
  switch (typename) {
    case 'TwitterSocialProfile':
      return Network.TWITTER;
    case 'PinterestSocialProfile':
      return Network.PINTEREST;
    case 'LinkedInSocialProfile':
      return Network.LINKEDIN;
    case 'FacebookSocialProfile':
      return Network.FACEBOOK;
    case 'InstagramSocialProfile':
      return Network.INSTAGRAM;
    case 'GoogleSocialProfile':
      return Network.GOOGLE;
    case 'TikTokSocialProfile':
      return Network.TIKTOK;
    case 'SocialProfile':
    case 'GenericSocialProfile':
    default:
      return Network.GENERIC_PROFILE;
  }
};

const mapOAuthFromTypename = (
  typename: SocialProfileFieldsFragment['__typename'] | CoreBitlyFieldsFragment['__typename'],
  apiType?: InstagramApiType
): SocialOAuthProvider | null => {
  switch (typename) {
    case 'TwitterSocialProfile':
      return SocialOAuthProvider.TWITTER;
    case 'PinterestSocialProfile':
      return SocialOAuthProvider.PINTEREST;
    case 'LinkedInSocialProfile':
      return SocialOAuthProvider.LINKEDIN;
    case 'FacebookSocialProfile':
      return SocialOAuthProvider.FACEBOOK;
    case 'InstagramSocialProfile':
      return apiType === InstagramApiType.FACEBOOK_GRAPH ? SocialOAuthProvider.FACEBOOK : SocialOAuthProvider.INSTAGRAM;
    case 'GoogleSocialProfile':
      return SocialOAuthProvider.GOOGLE;
    case 'Bitly':
      return SocialOAuthProvider.BITLY;
    case 'TikTokSocialProfile':
      return SocialOAuthProvider.TIKTOK;
    default:
      return null;
  }
};
const getOAuthProviderTypename = (
  typename: SocialProfileFieldsFragment['__typename'] | CoreBitlyFieldsFragment['__typename']
): SocialOAuthProvider | null => {
  switch (typename) {
    case 'TwitterSocialProfile':
      return SocialOAuthProvider.TWITTER;
    case 'PinterestSocialProfile':
      return SocialOAuthProvider.PINTEREST;
    case 'LinkedInSocialProfile':
      return SocialOAuthProvider.LINKEDIN;
    case 'FacebookSocialProfile':
      return SocialOAuthProvider.FACEBOOK;
    case 'InstagramSocialProfile':
      return SocialOAuthProvider.INSTAGRAM;
    case 'GoogleSocialProfile':
      return SocialOAuthProvider.GOOGLE;
    case 'Bitly':
      return SocialOAuthProvider.BITLY;
    case 'TikTokSocialProfile':
      return SocialOAuthProvider.TIKTOK;
    case 'SocialProfile':
    default:
      return null;
  }
};

const getSocialNetworkFromTypename = (
  typename: SocialProfileFieldsFragment['__typename'] | CoreBitlyFieldsFragment['__typename']
): SocialNetworksEnum | null => {
  switch (typename) {
    case 'TwitterSocialProfile':
      return SocialNetworksEnum.twitter;
    case 'PinterestSocialProfile':
      return SocialNetworksEnum.pinterest;
    case 'LinkedInSocialProfile':
      return SocialNetworksEnum.linkedin;
    case 'FacebookSocialProfile':
      return SocialNetworksEnum.facebook;
    case 'InstagramSocialProfile':
      return SocialNetworksEnum.instagram;
    case 'GoogleSocialProfile':
      return SocialNetworksEnum.google;
    case 'Bitly':
      return SocialNetworksEnum.bitly;
    case 'TikTokSocialProfile':
      return SocialNetworksEnum.tiktok;
    case 'SocialProfile':
    default:
      return null;
  }
};

const getApolloSocialProfileTypeFromTypename = (
  typename:
    | SocialProfileFieldsFragment['__typename']
    | NonNullable<ReconnectSocialProfile_reconnectSocialProfile_socialProfile>['__typename']
    | NonNullable<ConnectMainSocialProfile_connectMainSocialProfile_socialProfile>['__typename']
): ApolloErrorMapTypesType => {
  switch (typename) {
    case 'TwitterSocialProfile':
      return 'TWITTER_SOCIAL_PROFILE';
    case 'FacebookSocialProfile':
      return 'FACEBOOK_SOCIAL_PROFILE';
    case 'PinterestSocialProfile':
      return 'PINTEREST_SOCIAL_PROFILE';
    case 'LinkedInSocialProfile':
      return 'LINKED_IN_SOCIAL_PROFILE';
    case 'InstagramSocialProfile':
      return 'INSTAGRAM_SOCIAL_PROFILE';
    case 'GoogleSocialProfile':
      return 'GOOGLE_SOCIAL_PROFILE';
    case 'SocialProfile':
    default:
      return 'SOCIAL_PROFILE';
  }
};

const reconnectReducer: Reducer<FacebookReconnectType, FacebookReconnectAction> = (state, action) => {
  switch (action.type) {
    case 'queryToken':
      return { ...state, authTokenResult: action.payload };
    case 'setSocialProfileAndOAuth':
      return { ...state, socialProfile: action.payload.socialProfile, socialOAuth: action.payload.socialOAuth };
    case 'reset':
      return FacebookReconnectInitialState;
    default:
      return state;
  }
};

const getDisplayProfileName = (provider: SocialOAuthProvider, network?: Network) => {
  switch (provider) {
    case SocialOAuthProvider.LINKEDIN:
      return 'LinkedIn';
    case SocialOAuthProvider.TWITTER:
      return 'X (Twitter)';
    case SocialOAuthProvider.TIKTOK:
      return 'TikTok';
    case SocialOAuthProvider.FACEBOOK:
      return network === Network.INSTAGRAM ? 'Instagram' : 'Facebook';
    default:
      return capitalize(provider);
  }
};

const getWorkspaceNameById = (wsid: string, organization: GetMe_me_organizations): string => {
  return organization.workspaces.reduce((prev, current) => {
    if (prev) return prev;
    return current.id === wsid ? current.name : prev;
  }, '');
};

const getFirstProfileInvalid = (profiles: SelectedSocialProfileValue[]) => {
  return profiles.find(profile => {
    if (profile.__typename === 'TikTokSocialProfile' || profile.__typename === 'PinterestSocialProfile') {
      return !(
        (profile.__typename === 'TikTokSocialProfile' && profile.tikTok && profile.tikTok.privacyTarget) ||
        (profile.__typename === 'PinterestSocialProfile' && profile.pnBoardId)
      );
    } else {
      return false;
    }
  });
};

const isSocialOAuthProvider = (provider: string): provider is SocialOAuthProvider => {
  return Object.values(SocialOAuthProvider).includes(provider as SocialOAuthProvider);
};

const isGenericProfileType = (profile: string): profile is GenericProfileType => {
  return Object.values(GenericProfileType).includes(profile as GenericProfileType);
};

const getNetwork = (profile: Partial<SocialProfileComponentBasicType>): Network | undefined => {
  return Object.values(Network).includes(profile.network as Network) ? (profile.network as Network) : undefined;
};

const getGenericProfile = (profile: Partial<SocialProfileComponentBasicType>): GenericProfileType | undefined => {
  return Object.values(GenericProfileType).includes(profile.genericProfile as GenericProfileType)
    ? (profile.genericProfile as GenericProfileType)
    : undefined;
};

const getCacheIdForSocialProfile = (provider: SocialOAuthProvider | 'GenericSocialProfile', id: string): string => {
  // TODO would be good to use typename here instead.
  switch (provider) {
    case SocialOAuthProvider.TWITTER:
      return `TwitterSocialProfile:${id}`;
    case SocialOAuthProvider.GOOGLE:
      return `GoogleSocialProfile:${id}`;
    case SocialOAuthProvider.LINKEDIN:
      return `LinkedInSocialProfile:${id}`;
    case SocialOAuthProvider.FACEBOOK:
      return `FacebookSocialProfile:${id}`;
    case SocialOAuthProvider.INSTAGRAM:
      return `InstagramSocialProfile:${id}`;
    case SocialOAuthProvider.PINTEREST:
      return `PinterestSocialProfile:${id}`;
    case SocialOAuthProvider.TIKTOK:
      return `TikTokSocialProfile:${id}`;
    case SocialOAuthProvider.THREADS:
      return `ThreadsSocialProfile:${id}`;
    case 'GenericSocialProfile':
      return `GenericSocialProfile:${id}`;
    default:
      throw Error("getCacheIdFromNetwork() doesn't support type " + provider);
  }
};

const getProfileCacheId = (socialProfile: { provider: SocialOAuthProvider; network: Network; id: string }) => {
  if (!socialProfile.network || !socialProfile.provider || socialProfile.provider === SocialOAuthProvider.BITLY) {
    return undefined;
  }

  if (isSocialOAuthProvider(socialProfile.provider)) {
    return getCacheIdForSocialProfile(socialProfile.provider, socialProfile.id);
  }

  if (isGenericProfileType(socialProfile.provider)) {
    return getCacheIdForSocialProfile('GenericSocialProfile', socialProfile.id);
  }

  return undefined;
};

export const profilesHelpers = {
  getMentionType,
  reconnectReducer,
  remapMentionTagsToSocialProfile,
  remapSocialProfile,
  sortProfiles,
  setNetworkAndCallOAuth,
  getSocialNetworkFromTypename,
  getApolloSocialProfileTypeFromTypename,
  getOAuthProviderTypename,
  getDisplayProfileName,
  getWorkspaceNameById,
  getFirstProfileInvalid,
  mapNetworkFromTypename,
  mapOAuthFromTypename,
  mapGenericProfileToTypename,
  isSocialOAuthProvider,
  isGenericProfileType,
  getNetwork,
  getGenericProfile,
  getCacheIdForSocialProfile,
  getProfileCacheId
};
